import { useReduxState } from 'hooks';
import { useFormState } from 'informed';
import { Checkbox, Select, Text, TextArea } from 'components/common/form';

import type {
  CustomAttributes,
  ProductUserCustomAttribute
} from 'shared-types';

interface Props {
  attribute: Pick<
    ProductUserCustomAttribute,
    | 'editableBy'
    | 'id'
    | 'isRequired'
    | 'max'
    | 'min'
    | 'options'
    | 'position'
    | 'title'
    | 'titleTranslations'
    | 'type'
  >;
  fieldParent?: string;
  ignoreRequired?: boolean;
}

const ProductUserCustomAttributeRenderFormField = ({
  attribute,
  fieldParent,
  ignoreRequired = false
}: Props) => {
  const { language, messages } = useReduxState();

  const formState = useFormState();

  const { id, max, min, options, isRequired, title, titleTranslations, type } =
    attribute;

  const validate = (value: CustomAttributes[0]) => {
    const isEmpty =
      (value !== 0 && !value) || (Array.isArray(value) && !value.length);

    return isEmpty ? messages.commonFieldRequired : undefined;
  };

  const propsToPass: {
    field: string;
    [key: string]: any;
  } = {
    error: fieldParent
      ? formState.errors?.[fieldParent]?.[`userAttributes-${id}`]
      : formState.errors?.[`userAttributes-${id}`],
    field: `${fieldParent ? `${fieldParent}.` : ''}userAttributes-${id}`,
    id: `product-user-custom-attribute-${id}-${fieldParent || ''}`,
    ...(options?.length
      ? {
          options: options.map((option) => ({
            label: option,
            value: option
          }))
        }
      : {}),
    ...(isRequired && !ignoreRequired
      ? { validate, validateOnChange: true }
      : {})
  };

  switch (type) {
    case 'Array':
      propsToPass.isSearchable = true;
      propsToPass.isMulti = true;
      propsToPass.creatable = true;
      if (!options) propsToPass.options = []; // options cannot be undefined
      break;
    case 'ArrayWithOptions':
      propsToPass.isSearchable = true;
      propsToPass.isMulti = true;
      if (!options) propsToPass.options = []; // options cannot be undefined
      break;
    case 'Boolean':
      if (ignoreRequired) {
        propsToPass.options = [
          { label: messages.commonYes, value: true },
          { label: messages.commonNo, value: false }
        ];
      } else {
        propsToPass.label = titleTranslations?.[language] || title;
        propsToPass.style = { display: 'inline-block' };
      }
      break;
    case 'Number':
      propsToPass.isSearchable = false;
      // @ts-ignore
      propsToPass.options = [...Array(max - min + 1).keys()].map((n) => ({
        label: n + min,
        value: n + min
      }));
      propsToPass.style = { maxWidth: 120 };
      break;
    case 'StringWithOptions':
      propsToPass.isSearchable = true;
      propsToPass.style = { maxWidth: 300 };
      if (!isRequired) propsToPass.isClearable = true;
      if (!options) propsToPass.options = []; // options cannot be undefined
      break;
    case 'Text':
      propsToPass.autoGrow = true;
      break;
    default:
      propsToPass.type = 'text';
      break;
  }

  return type === 'Boolean' && !propsToPass.options ? (
    <Checkbox {...propsToPass} />
  ) : type === 'Text' ? (
    <TextArea {...propsToPass} />
  ) : type === 'Array' || propsToPass.options ? (
    <Select {...propsToPass} />
  ) : (
    <Text {...propsToPass} />
  );
};

export default ProductUserCustomAttributeRenderFormField;
